import React, { useState } from 'react';
import { motion } from 'framer-motion';
import useDashboardStore from '../store/dashboardStore';

const LandingPage = ({ onLogin, onSignup, onDemo }) => {
  const { getPlanConfig, planDisplayOrder } = useDashboardStore((state) => ({
    getPlanConfig: state.getPlanConfig,
    planDisplayOrder: state.planDisplayOrder,
  }));

  const [activeSection, setActiveSection] = useState('hero');

  const features = [
    {
      icon: '\u{1F916}',
      title: 'AI-Powered Agents',
      description: '184+ intelligent agents monitoring marketplaces 24/7, finding profitable opportunities automatically.',
    },
    {
      icon: '\u{1F50D}',
      title: 'Multi-Marketplace Scanning',
      description: 'Scan 83+ marketplaces including Amazon, eBay, Walmart, and more from one unified dashboard.',
    },
    {
      icon: '\u{1F4CA}',
      title: 'Real-Time Analytics',
      description: 'Live revenue tracking, profit margins, and performance metrics updated every second.',
    },
    {
      icon: '\u26A1',
      title: 'Instant Alerts',
      description: 'Get notified immediately when prices drop or new arbitrage opportunities appear.',
    },
    {
      icon: '\u{1F517}',
      title: 'App Ecosystem',
      description: 'Connect with Fire POS, Tea Effects, PhantomCast, and more - all from one platform.',
    },
    {
      icon: '\u{1F512}',
      title: 'Enterprise Security',
      description: 'Bank-level encryption, SOC 2 compliance, and role-based access control.',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'E-commerce Director',
      company: 'TechRetail Inc',
      image: null,
      quote: 'QuantrolixAI increased our profit margins by 34% in the first month. The AI agents work around the clock finding deals we would have missed.',
    },
    {
      name: 'Marcus Johnson',
      role: 'Founder',
      company: 'ArbitrageKing',
      image: null,
      quote: 'I replaced 3 separate tools with Quantrolix. The unified dashboard saves me hours every day and the ROI is incredible.',
    },
    {
      name: 'Elena Rodriguez',
      role: 'Operations Manager',
      company: 'Global Goods Co',
      image: null,
      quote: 'The multi-marketplace scanning is a game changer. We now source from 50+ marketplaces we never knew existed.',
    },
  ];

  const stats = [
    { value: '$2.4M+', label: 'Revenue Generated' },
    { value: '184', label: 'AI Agents Active' },
    { value: '83+', label: 'Marketplaces' },
    { value: '99.9%', label: 'Uptime SLA' },
  ];

  const quantrolixApps = [
    { name: 'Fire POS', icon: '\u{1F525}', desc: 'Restaurant Point of Sale', price: '$29/mo' },
    { name: 'Tea Effects', icon: '\u{1F375}', desc: 'Tea Recommendation Engine', price: '$5/mo' },
    { name: 'PhantomCast', icon: '\u{1F4FA}', desc: 'Streaming TV Platform', price: 'Free tier' },
    { name: 'Menu Mentor', icon: '\u{1F4DA}', desc: 'Staff Training SaaS', price: '$19/mo' },
    { name: 'Bourbon Hunter', icon: '\u{1F943}', desc: 'Collector Price Tracker', price: '$9/mo' },
    { name: 'Kasa Controller', icon: '\u{1F3E0}', desc: 'Smart Home Automation', price: '$4.99/mo' },
  ];

  return (
    <div className="landing-page" style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%)',
      color: 'white',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'rgba(10, 10, 15, 0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(102, 126, 234, 0.2)',
        zIndex: 1000,
      }}>
        <div style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          fontFamily: 'Orbitron, monospace',
          background: 'linear-gradient(135deg, #667eea, #00d4ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          QuantrolixAI
        </div>
        <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
          <a href="#features" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none' }}>Features</a>
          <a href="#pricing" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none' }}>Pricing</a>
          <a href="#apps" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none' }}>Apps</a>
          <button onClick={onLogin} style={{
            padding: '10px 20px',
            background: 'transparent',
            border: '1px solid rgba(102, 126, 234, 0.5)',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
          }}>
            Sign In
          </button>
          <button onClick={onSignup} style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #667eea, #00d4ff)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
            fontWeight: '600',
          }}>
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '120px 40px 80px',
      }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: 'rgba(102, 126, 234, 0.2)',
            borderRadius: '20px',
            marginBottom: '24px',
            fontSize: '0.9rem',
            color: '#667eea',
          }}>
            Trusted by 500+ e-commerce businesses
          </div>
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: '800',
            lineHeight: '1.1',
            marginBottom: '24px',
            maxWidth: '900px',
          }}>
            <span style={{
              background: 'linear-gradient(135deg, #667eea, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>AI-Powered</span> Marketplace
            <br />Intelligence Platform
          </h1>
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255,255,255,0.7)',
            maxWidth: '600px',
            margin: '0 auto 40px',
            lineHeight: '1.6',
          }}>
            Automate product scanning, price monitoring, and arbitrage discovery across 83+ marketplaces with our intelligent AI agents.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={onSignup} style={{
              padding: '16px 32px',
              fontSize: '1.1rem',
              background: 'linear-gradient(135deg, #667eea, #00d4ff)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              cursor: 'pointer',
              fontWeight: '600',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
            }}>
              Get Started
            </button>
            <button onClick={onDemo} style={{
              padding: '16px 32px',
              fontSize: '1.1rem',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              color: 'white',
              cursor: 'pointer',
            }}>
              Watch Demo
            </button>
          </div>
          <p style={{
            marginTop: '16px',
            fontSize: '0.9rem',
            color: 'rgba(255,255,255,0.5)',
          }}>
            Free forever -- no credit card required
          </p>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section style={{
        padding: '60px 40px',
        background: 'rgba(102, 126, 234, 0.05)',
        borderTop: '1px solid rgba(102, 126, 234, 0.2)',
        borderBottom: '1px solid rgba(102, 126, 234, 0.2)',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '40px',
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
        }}>
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <div style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea, #00d4ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                {stat.value}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.6)', marginTop: '8px' }}>
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" style={{
        padding: '100px 40px',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            marginBottom: '16px',
          }}>
            Everything You Need to
            <span style={{
              background: 'linear-gradient(135deg, #667eea, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}> Scale</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem' }}>
            Powerful features designed for serious e-commerce businesses
          </p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '30px',
        }}>
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              style={{
                padding: '30px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '16px',
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.5)';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>{feature.icon}</div>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '12px' }}>{feature.title}</h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6' }}>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" style={{
        padding: '100px 40px',
        background: 'rgba(10, 10, 15, 0.5)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            marginBottom: '16px',
          }}>
            Simple, Transparent
            <span style={{
              background: 'linear-gradient(135deg, #667eea, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}> Pricing</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem' }}>
            Plans for every stage of growth. 14-day money-back guarantee.
          </p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '24px',
          maxWidth: '1100px',
          margin: '0 auto',
        }}>
          {planDisplayOrder.map((key, i) => {
            const plan = getPlanConfig(key);
            const isPopular = plan?.is_popular;
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                style={{
                  padding: '32px 28px',
                  background: isPopular
                    ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.2), rgba(102, 126, 234, 0.2))'
                    : 'rgba(255,255,255,0.03)',
                  border: `2px solid ${isPopular ? '#9333ea' : 'rgba(102, 126, 234, 0.2)'}`,
                  borderRadius: '20px',
                  transform: isPopular ? 'scale(1.05)' : 'scale(1)',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {isPopular && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #9333ea, #667eea)',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                  }}>
                    MOST POPULAR
                  </div>
                )}
                <h3 style={{
                  color: isPopular ? '#9333ea' : '#00d4ff',
                  fontFamily: 'Orbitron, monospace',
                  marginBottom: '8px',
                  marginTop: isPopular ? '8px' : '0',
                }}>
                  {plan?.name}
                </h3>
                <p style={{
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: '0.85rem',
                  lineHeight: '1.4',
                  minHeight: '40px',
                  marginBottom: '16px',
                }}>
                  {plan?.description}
                </p>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  marginBottom: '20px',
                }}>
                  ${plan?.price}<span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)' }}>/mo</span>
                </div>
                <div style={{
                  borderTop: '1px solid rgba(102, 126, 234, 0.2)',
                  paddingTop: '20px',
                  flex: 1,
                }}>
                  {(plan?.features || []).slice(0, 6).map((feature, idx) => (
                    <div key={idx} style={{ marginBottom: '12px', color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                      <span style={{ color: isPopular ? '#9333ea' : '#00d4ff', marginRight: '8px' }}>&#10003;</span>
                      {feature}
                    </div>
                  ))}
                </div>
                <button onClick={onSignup} style={{
                  width: '100%',
                  padding: '14px',
                  marginTop: '20px',
                  background: isPopular
                    ? 'linear-gradient(135deg, #9333ea, #667eea)'
                    : 'linear-gradient(135deg, #667eea, #00d4ff)',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                }}>
                  {plan?.cta || 'Get Started'}
                </button>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Apps Ecosystem Section */}
      <section id="apps" style={{
        padding: '100px 40px',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            marginBottom: '16px',
          }}>
            The Quantrolix
            <span style={{
              background: 'linear-gradient(135deg, #667eea, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}> Ecosystem</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem' }}>
            One subscription, access to all our premium apps
          </p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
        }}>
          {quantrolixApps.map((app, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
              style={{
                padding: '24px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div style={{ fontSize: '2rem' }}>{app.icon}</div>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>{app.name}</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>{app.desc}</div>
                <div style={{ color: '#00d4ff', fontSize: '0.85rem', marginTop: '4px' }}>{app.price}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section style={{
        padding: '100px 40px',
        background: 'rgba(102, 126, 234, 0.05)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            marginBottom: '16px',
          }}>
            Loved by
            <span style={{
              background: 'linear-gradient(135deg, #667eea, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}> Thousands</span>
          </h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '30px',
          maxWidth: '1200px',
          margin: '0 auto',
        }}>
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              style={{
                padding: '30px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '16px',
              }}
            >
              <p style={{
                color: 'rgba(255,255,255,0.8)',
                lineHeight: '1.6',
                marginBottom: '20px',
                fontStyle: 'italic',
              }}>
                "{t.quote}"
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea, #00d4ff)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                }}>
                  {t.name[0]}
                </div>
                <div>
                  <div style={{ fontWeight: '600' }}>{t.name}</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                    {t.role}, {t.company}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '100px 40px',
        textAlign: 'center',
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          style={{
            maxWidth: '800px',
            margin: '0 auto',
            padding: '60px',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(147, 51, 234, 0.2))',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            borderRadius: '24px',
          }}
        >
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            marginBottom: '16px',
          }}>
            Ready to Scale Your Business?
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: '1.1rem',
            marginBottom: '30px',
          }}>
            Join 500+ e-commerce businesses already using QuantrolixAI to find profitable opportunities.
          </p>
          <button onClick={onSignup} style={{
            padding: '18px 40px',
            fontSize: '1.2rem',
            background: 'linear-gradient(135deg, #667eea, #00d4ff)',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            cursor: 'pointer',
            fontWeight: '600',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
          }}>
            Get Started
          </button>
          <p style={{
            marginTop: '14px',
            fontSize: '0.9rem',
            color: 'rgba(255,255,255,0.5)',
          }}>
            14-day money-back guarantee on all plans
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '60px 40px',
        borderTop: '1px solid rgba(102, 126, 234, 0.2)',
        textAlign: 'center',
      }}>
        <div style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          fontFamily: 'Orbitron, monospace',
          background: 'linear-gradient(135deg, #667eea, #00d4ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '20px',
        }}>
          QuantrolixAI
        </div>
        <div style={{
          display: 'flex',
          gap: '30px',
          justifyContent: 'center',
          marginBottom: '30px',
          flexWrap: 'wrap',
        }}>
          <a href="#" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Privacy Policy</a>
          <a href="#" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Terms of Service</a>
          <a href="#" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Contact</a>
          <a href="#" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Support</a>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>
          &copy; 2026 QuantrolixAI. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default LandingPage;
